import { ReactNode } from 'react'

import { WidgetEmptyState } from '@factorialco/f0-react/dist/experimental'
import { ClockInWidgetEmployeesSettingFragment, TimesheetPageShiftFragment } from 'generated/resources/graphql'

type AttendanceBreakSetting = NonNullable<
  NonNullable<ClockInWidgetEmployeesSettingFragment['attendanceBreakSetting']>
>

import i18n from 'modules/core/lib/i18n'

import BreakSettingsUtils from 'modules/attendance/lib/utils/BreakSettings'
import {
  ErrorTypes,
  ValidationError,
  formatBreakTime,
} from 'modules/attendance/lib/validateShiftBreakDurationRules'

export interface Props {
  breakSetting: AttendanceBreakSetting | null
  todayShifts: TimesheetPageShiftFragment[]
  hasCorrectiveBreaksFeature: boolean
  alertTypeToCheck: ErrorTypes | null
  onDismiss: () => void
  onReview: () => void
}

const buildRestTimeDescription = (
  alert: ValidationError,
  breakShifts: TimesheetPageShiftFragment[],
  hasAutofixMissingBreaks: boolean
): string | null => {
  const expectedBreakMinutes = alert.minimalDuration ?? 0
  const periodLengthText = formatBreakTime(alert.periodLength ?? 0)
  const totalBreaksTaken = breakShifts.reduce((acc, shift) => acc + shift.minutes, 0) ?? 0
  const breaksDiff = expectedBreakMinutes - totalBreaksTaken
  const correctiveMinutes = breaksDiff > 0 ? breaksDiff : 0

  if (hasAutofixMissingBreaks && correctiveMinutes > 0) {
    const correctiveMinutesText = formatBreakTime(correctiveMinutes)
    return i18n.t('clock_in.widget.break_alerts.on_clock_out.description_with_autofix', {
      period_length: periodLengthText,
      corrective_minutes: correctiveMinutesText,
    })
  }

  const breakDurationText = formatBreakTime(expectedBreakMinutes)
  return i18n.t('clock_in.widget.break_alerts.on_clock_out.description', {
    period_length: periodLengthText,
    break_duration: breakDurationText,
  })
}

const buildBreakMinimumDescription = (
  alert: ValidationError,
  breakShifts: TimesheetPageShiftFragment[]
): string => {
  const expectedBreakMinutes = alert.minimalDuration ?? 0
  const lastBreakDuration = breakShifts.at(-1)?.minutes ?? 0
  const breaksDiff = expectedBreakMinutes - lastBreakDuration
  const remainingMinutes = breaksDiff > 0 ? breaksDiff : 0
  const remainingMinutesText = formatBreakTime(remainingMinutes)
  const breakDurationText = formatBreakTime(expectedBreakMinutes)
  return i18n.t('clock_in.widget.break_alerts.on_break_end.description', {
    break_duration: breakDurationText,
    remaining_minutes: remainingMinutesText,
  })
}

const buildDescription = (
  alert: ValidationError,
  breakShifts: TimesheetPageShiftFragment[],
  hasAutofixMissingBreaks: boolean
): string | null => {
  if (alert.error === ErrorTypes.REST_TIME) {
    return buildRestTimeDescription(alert, breakShifts, hasAutofixMissingBreaks)
  }
  if (alert.error === ErrorTypes.BREAK_MINIMUM) {
    return buildBreakMinimumDescription(alert, breakShifts)
  }
  return null
}

const useMissingBreakWidget = ({
  breakSetting,
  todayShifts,
  hasCorrectiveBreaksFeature,
  alertTypeToCheck,
  onDismiss,
  onReview,
}: Props): ReactNode => {
  const shouldCheckForMissingBreak =
    !!alertTypeToCheck &&
    hasCorrectiveBreaksFeature &&
    BreakSettingsUtils.hasManualBreaksWithRules(breakSetting) &&
    todayShifts.length > 0

  if (!shouldCheckForMissingBreak) return null

  const missingBreakAlerts = BreakSettingsUtils.missingBreaksAlerts(breakSetting, todayShifts)
  const currentBreakAlert =
    missingBreakAlerts.find((alert) => alert.error === alertTypeToCheck) ?? null

  if (!currentBreakAlert) return null

  const hasAutofixMissingBreaks = BreakSettingsUtils.hasAutofixMissingBreaks(breakSetting)
  const breakShifts = todayShifts.filter((shift) => !shift.workable)

  const description = buildDescription(currentBreakAlert, breakShifts, hasAutofixMissingBreaks)
  if (!description) return null

  const title = i18n.t('clock_in.widget.break_alerts.title')

  return (
    <WidgetEmptyState
      title={title}
      description={description}
      actions={[
        {
          label: i18n.t('clock_in.widget.break_alerts.review_timesheet'),
          onClick: onReview,
        },
        {
          label: i18n.t('clock_in.widget.take_break.ignore'),
          variant: 'outline',
          onClick: onDismiss,
        },
      ]}
    />
  )
}

export default useMissingBreakWidget
